import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTransition, animated } from "@react-spring/web";
import { Dropdown } from "react-bootstrap";
import pluralize from "pluralize";
import { useDispatch } from "react-redux";

import Actions from "src/components/actionbar";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import { DealRoomsContext } from "../..";
import { DealRoom } from "src/types/dealRoom";
import { CreateDealRoomDialog } from "../create";

import userRoleService from "src/services/userRoles.service";
import Toolbar from "src/components/toolbar";

import Slider from "src/components/slider";
import { DealRoomCard, DeleteDealRoomCard } from "../card";
import { deleteDealRoom } from "src/redux/actions/dealRoom";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";
import subscriptionPlanService from "src/services/subscriptionPlan.service";

interface DealRoomsMenuBarProps {
  show: boolean;
  query: string | null;
  // eslint-disable-next-line no-unused-vars
  handleSearchChange: (query: string | null) => void;
  // eslint-disable-next-line no-unused-vars
  handleEditModeChange: (editMode: 'rename' | 'delete' | null) => void;
  setCreateDealRoom: (show: boolean) => void;
}

const DealRoomsEditMenuBar: React.FC<DealRoomsMenuBarProps> = ({
  show, query, handleSearchChange, handleEditModeChange, setCreateDealRoom
}) => {
  const dispatch = useDispatch();
  const { trackRenameCollectionClicked, trackDeleteCollectionClicked, trackCreateCollectionClicked } = useCollectionsAdoption();

  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from: { opacity: 1, top: "-90px" },
    enter: { opacity: 1, top: "0px" },
    leave: { opacity: 0, top: "-90px" },
  }));

  const seekEnabled = useSelector((state: any) => _.get(state, 'accounts.item.seekEnabled', false));
  const hasDealRoomAccess = subscriptionPlanService.getViewerTypeList().includes('DEAL_ROOM');

  useEffect(() => {
    api.start();
  }, [show]);

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={cx(styles.dealRoomMenuBar)}>
          <Actions.Container>
            <Actions.Row>
              {userRoleService.canAccessSearch() && (
                <Actions.Search query={query} onSearch={handleSearchChange} />
              )}
              {seekEnabled && (
                <Actions.Seek />
              )}
            </Actions.Row>
            <Actions.Row>
              {userRoleService.canAccessDealRoom() && <Actions.IconButton
                icon="paperflite-rename"
                tooltip="Rename"
                onClick={() => {
                  handleEditModeChange("rename")
                  trackRenameCollectionClicked();
                }}
              />}
              {userRoleService.canAccessDealRoom() && <Actions.IconButton
                icon="paperflite-delete"
                tooltip="Delete"
                onClick={() => {
                  handleEditModeChange("delete")
                  trackDeleteCollectionClicked();
                }}
              />}

              {userRoleService.canCreateDealRoom() && userRoleService.canShare() && hasDealRoomAccess && (
                <Actions.Button
                  width={4}
                  icon={"paperflite-add"}
                  title="create a new deal room"
                  onClick={() => {
                    setCreateDealRoom(true)
                    trackCreateCollectionClicked()
                  }}
                  className={styles.createDealRoomIcon}
                />
              )}
            </Actions.Row>
          </Actions.Container>
    </animated.div>
  ));
}

interface RenameMenubarProps {
  show: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const RenameMenubar: React.FC<RenameMenubarProps> = ({
  show,
  onConfirm,
  onCancel,
}) => {
  const renaming = useSelector((state: any) => {
    const dealRoomUpdates = _.keys(state.dealRooms.update).filter((key) => {
      const item = state.dealRooms.update[key];
      return item.updating;
    });

    return _.size(dealRoomUpdates) > 0;
  });

  useEffect(() => {
    if (!renaming) {
      onCancel();
    }
  }, [renaming]);

  return (
    <Reveal show={show} className={styles.renameConfirmation}>
      <div className={styles.message}>
        All changes made to this section will reflect for all users in the
        content hub.
      </div>
      <Button
        variant="primary"
        text={renaming ? "Saving..." : "Save"}
        onClick={onConfirm}
      />
      <Button variant="secondary" text="Cancel" onClick={onCancel} />
    </Reveal>
  );
};

interface DeleteMenubarProps {
  show: boolean;
  onCancel: () => void;
  resetEdits: () => void;
}

const DeleteMenubar: React.FC<DeleteMenubarProps> = ({ show, onCancel, resetEdits }) => {
  const deleting = false;
  const [confirm, setConfirm] = React.useState(false);

  const { edits } = React.useContext(DealRoomsContext);
  const dealRooms = edits
    .filter((edit) => edit.value)
    .map((edit) => edit.value as DealRoom);

  const onConfirm = () => {
    if (_.size(dealRooms) == 0) {
      return;
    }

    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onCancel();
  };

  return (
    <Reveal show={show} className={styles.deleteConfirmation}>
      <div className={styles.message}>
        Select rooms to delete
      </div>
      <Button
        variant="primary"
        icon="paperflite-delete"
        text={deleting ? "Deleting..." : "Delete"}
        disabled={_.size(dealRooms) == 0}
        onClick={onConfirm}
      />
      <Button variant="secondary" text="Cancel" onClick={onCancel}></Button>

      <DeleteConfirmationDialog
        show={confirm}
        dealRooms={dealRooms}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
        resetEdits={resetEdits}
      />
    </Reveal>
  );
};

const SORT_BY_VALUES = [
  {
    label: "All deal rooms",
    value: "all",
  },
  {
    label: "Created by me",
    value: "owned_by_me",
  },
  {
    label: "Collaborated with me",
    value: "collaborated",
  },
];

interface filterDropdownProps {
  filterBy: string;
  totalRecordsCount: number;
  handleFilterChange: (filterBy: string) => void;
}

const FilterDropdown: React.FC<filterDropdownProps> = ({
  totalRecordsCount,
  filterBy,
  handleFilterChange,
}) => {
  const [selectedFilter, setSelectedFilter] = React.useState(SORT_BY_VALUES[0]);

  useEffect(() => {
    const filter = SORT_BY_VALUES.find((item) => item.value === filterBy);
    setSelectedFilter(filter || SORT_BY_VALUES[0]);
  }, [filterBy]);

  return (
    <div className={styles.filterDropdown}>
      <Dropdown>
        <Dropdown.Toggle className={styles.button} as={"button"}>
          {selectedFilter.label}{" "}
          {totalRecordsCount >= 0 && `(${totalRecordsCount})`}{" "}
          <i className={cx(styles.icon, "paperflite-dropdown")}></i>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={styles.dropdown}
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {SORT_BY_VALUES.map((item, index) => {
            const active = item.value === filterBy;
            return (
              <Dropdown.Item
                key={index}
                className={cx(styles.dropdownItem, { [styles.active]: active })}
                onClick={() => handleFilterChange(item.value)}
              >
                {item.label}
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

interface DeleteSelectionToolbarProps {
  show: boolean;
  dealRooms: DealRoom[];
  onFinish: () => void;
}

export const DeleteSelectionToolbar: React.FC<DeleteSelectionToolbarProps> = ({
  show,
  dealRooms,
  onFinish,
}) => {
  const [confirm, setConfirm] = React.useState(false);

  const handleShowConfirm = () => {
    setConfirm(true);
  };

  const handleConfirm = () => {
    setConfirm(false);
    onFinish();
  };

  return (
    <>
      <Toolbar.Container show={show}>
        <Toolbar.Text>
          <span>{_.size(dealRooms)} Selected</span>
        </Toolbar.Text>
        <Toolbar.Divider />
        <Toolbar.IconButton
          icon="fa-regular fa-trash-can"
          onClick={handleShowConfirm}
        />
      </Toolbar.Container>

      <DeleteConfirmationDialog
        show={confirm}
        dealRooms={dealRooms}
        onFinish={handleConfirm}
        onClose={() => setConfirm(false)}
      />
    </>
  );
};

interface DeleteConfirmationDialogProps {
  show: boolean;
  dealRooms: DealRoom[];
  onFinish: () => void;
  onClose: () => void;
  resetEdits?: () => void;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  show,
  dealRooms,
  onFinish,
  onClose,
  resetEdits
}) => {
  const dispatch = useDispatch();
  const { trackCollectionDeleted } = useCollectionsAdoption();

  const deleting = useSelector((state: any) => {
    const dealRoomDeletions = _.keys(state.dealRooms.deletion).filter((key) => {
      const item = state.dealRooms.deletion[key];
      return item.deleting;
    });

    return _.size(dealRoomDeletions) > 0;
  });

  const deleted = useSelector((state: any) => {
    const dealRoomDeletions = _.keys(state.dealRooms.deletion).filter((key) => {
      const item = state.dealRooms.deletion[key];
      return item.deleted;
    });

    return _.size(dealRoomDeletions) > 0;
  });

  const handleConfirm = () => {
    if (_.size(dealRooms) == 0) {
      return;
    }

    dealRooms.forEach((dealRoom) => {
      if (dealRoom.id) {
        dispatch(
          deleteDealRoom({
            id: dealRoom.id,
          }),
        );
      }

      trackCollectionDeleted(dealRoom.name!);
    });
  };

  const handleCancel = () => {
    if (resetEdits) {
      resetEdits();
    }
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!deleting && deleted) {
      onFinish();
    }
  }, [deleting, deleted]);

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="400"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        title="delete selected deal room(s)?"
        message={`${_.size(dealRooms)} ${pluralize("deal room", _.size(dealRooms))}`}
      >
        <div className={styles.description}>
          This action cannot be undone and the deal room will no longer be accessible.
          <span className={styles.highlight}>Are you sure you want to proceed?</span>
        </div>
      </Slider.Header>
      <Slider.Body>
        <div className={styles.grid}>
          {dealRooms.map((dealRoom) => {
            return <DeleteDealRoomCard dealRoom={dealRoom} />;
          })}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={deleting ? "Removing..." : "Remove"}
          variant="primary"
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

export {
  DealRoomsEditMenuBar,
  RenameMenubar,
  DeleteMenubar,
  FilterDropdown,
  DeleteConfirmationDialog,
};
